export function queryHasKey(query, key) {
    return Object.prototype.hasOwnProperty.call(query || {}, key);
}

export function firstQueryValue(query, keys) {
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (let index = 0; index < keyList.length; index += 1) {
        const key = keyList[index];
        if (!queryHasKey(query, key)) {
            continue;
        }

        const value = query[key];
        if (Array.isArray(value)) {
            return value.length > 0 ? value[0] : '';
        }

        return value;
    }

    return null;
}

export function queryString(query, keys, fallback = '') {
    const value = firstQueryValue(query, keys);
    if (value === null || typeof value === 'undefined') {
        return fallback;
    }

    return String(value);
}

export function queryNumber(query, keys, fallback = null, allowedValues = null) {
    const rawValue = firstQueryValue(query, keys);
    if (rawValue === null || typeof rawValue === 'undefined' || String(rawValue).trim() === '') {
        return fallback;
    }

    const value = Number(rawValue);
    if (!Number.isFinite(value)) {
        return fallback;
    }

    if (Array.isArray(allowedValues) && allowedValues.indexOf(value) === -1) {
        return fallback;
    }

    return value;
}

export function queryNumberArray(query, keys, fallback = []) {
    const keyList = Array.isArray(keys) ? keys : [keys];
    let values = null;

    for (let index = 0; index < keyList.length; index += 1) {
        const key = keyList[index];
        if (!queryHasKey(query, key)) {
            continue;
        }

        values = query[key];
        break;
    }

    if (values === null || typeof values === 'undefined') {
        return Array.isArray(fallback) ? [...fallback] : [];
    }

    const rawValues = Array.isArray(values) ? values : [values];

    return rawValues
        .flatMap((value) => String(value || '').split(','))
        .map((value) => Number(String(value || '').trim()))
        .filter((value) => Number.isInteger(value) && value > 0)
        .filter((value, index, list) => list.indexOf(value) === index);
}

export function normalizeQueryNumberArray(value) {
    return (Array.isArray(value) ? value : [])
        .map((item) => Number(item))
        .filter((item) => Number.isInteger(item) && item > 0)
        .filter((item, index, list) => list.indexOf(item) === index);
}

export function sameNumberArray(left, right) {
    const normalizedLeft = normalizeQueryNumberArray(left).sort((first, second) => first - second);
    const normalizedRight = normalizeQueryNumberArray(right).sort((first, second) => first - second);

    if (normalizedLeft.length !== normalizedRight.length) {
        return false;
    }

    for (let index = 0; index < normalizedLeft.length; index += 1) {
        if (normalizedLeft[index] !== normalizedRight[index]) {
            return false;
        }
    }

    return true;
}

export function routeWithReturnTo(targetPath, currentRoute, fallbackPath) {
    const returnTo = encodeURIComponent((currentRoute && currentRoute.fullPath) ? currentRoute.fullPath : fallbackPath);
    const separator = String(targetPath || '').includes('?') ? '&' : '?';

    return String(targetPath || '') + separator + 'return_to=' + returnTo;
}

export function resolveReturnToPath(query, fallbackPath) {
    const rawReturnTo = Array.isArray(query && query.return_to)
        ? query.return_to[0]
        : (query ? query.return_to : null);
    const returnTo = String(rawReturnTo || '');
    const allowedBasePath = String(fallbackPath || '');

    if (returnTo === allowedBasePath || returnTo.startsWith(allowedBasePath + '?')) {
        return returnTo;
    }

    return allowedBasePath;
}

export function queryWithoutKeys(query, keys) {
    const removeKeys = new Set(keys || []);
    return Object.keys(query || {}).reduce((carry, key) => {
        if (!removeKeys.has(key)) {
            carry[key] = query[key];
        }

        return carry;
    }, {});
}

export function setQueryValue(query, key, value, defaultValue = null) {
    if (Array.isArray(value)) {
        if (Array.isArray(defaultValue) && sameNumberArray(value, defaultValue)) {
            return;
        }

        const normalizedValues = normalizeQueryNumberArray(value);
        query[key] = normalizedValues.length > 0 ? normalizedValues.map((item) => String(item)) : '';
        return;
    }

    if (value === null || typeof value === 'undefined' || String(value).trim() === '') {
        return;
    }

    if (defaultValue !== null && String(value) === String(defaultValue)) {
        return;
    }

    query[key] = String(value);
}

export function sameRouteQuery(left, right) {
    const leftKeys = Object.keys(left || {}).sort();
    const rightKeys = Object.keys(right || {}).sort();

    if (leftKeys.length !== rightKeys.length) {
        return false;
    }

    for (let index = 0; index < leftKeys.length; index += 1) {
        const key = leftKeys[index];
        if (key !== rightKeys[index]) {
            return false;
        }

        const leftValue = Array.isArray(left[key]) ? left[key].map(String).sort() : [String(left[key])];
        const rightValue = Array.isArray(right[key]) ? right[key].map(String).sort() : [String(right[key])];

        if (leftValue.length !== rightValue.length) {
            return false;
        }

        for (let valueIndex = 0; valueIndex < leftValue.length; valueIndex += 1) {
            if (leftValue[valueIndex] !== rightValue[valueIndex]) {
                return false;
            }
        }
    }

    return true;
}
